import { memo, type MouseEvent, useCallback, useEffect, useRef } from 'react';
import logTrailCardClicked from '@alltrails/amplitude/events/logTrailCardClicked';
import TrailStandardCard from '@alltrails/cards/components/TrailStandardCard';
import useIsVisible from '@alltrails/core/hooks/useIsVisible';
import { useLanguageRegionCode } from '@alltrails/core/hooks/useLanguageRegionCode';
import useReduxListItems from '@alltrails/lists/hooks/useReduxListItems';
import trailFromAlgoliaTrail from '@alltrails/trails/utils/trailFromAlgoliaTrail';
import type { TrailSearchResult } from '@alltrails/types/AlgoliaResults';
import useTrailNavigate, { useExploreTrailUrl } from '@/hooks/useTrailNavigate';
import { useLogExploreCardViewed } from '@/utils/analyticsHelpers';
import { useAppSelector } from '@/utils/redux';

type ListViewTrailCardProps = {
  index: number;
  onCardMouseEnter?: (trail: TrailSearchResult) => void;
  onCardMouseLeave?: () => void;
  onCompletionBadgeClick?: () => void;
  trail: TrailSearchResult;
} & Pick<ReturnType<typeof useReduxListItems>, 'handleFavoriteClick' | 'isComplete' | 'isFavorite' | 'isVerified'>;

const ListViewTrailCard = ({
  handleFavoriteClick,
  isComplete,
  isFavorite,
  isVerified,
  index,
  onCardMouseEnter,
  onCardMouseLeave,
  onCompletionBadgeClick,
  trail
}: ListViewTrailCardProps) => {
  const languageRegionCode = useLanguageRegionCode();
  const trailNavigate = useTrailNavigate();
  const exploreTrailUrl = useExploreTrailUrl();
  const { exploreServiceFilters } = useAppSelector(state => ({
    exploreServiceFilters: state.exploreService.filters
  }));

  const container = useRef<HTMLDivElement>(null);
  const isVisible = useIsVisible(container);
  const logExploreCardViewed = useLogExploreCardViewed('trail');

  const activityType = exploreServiceFilters?.activity?.[0];
  const cardTrail = trailFromAlgoliaTrail(trail, activityType);

  const onCardClick = useCallback(
    (e: MouseEvent<HTMLAnchorElement>) => {
      logTrailCardClicked({ trail_id: String(trail.ID), card_location: 'explore list view', detailed_card_location: 'explore tab list view' });
      trailNavigate(cardTrail.slug, e);
    },
    [trail, cardTrail.slug, trailNavigate]
  );

  const onCardMouseEnterInternal = useCallback(() => {
    onCardMouseEnter?.(trail);
  }, [onCardMouseEnter, trail]);

  useEffect(() => {
    if (isVisible) {
      logExploreCardViewed(trail.ID, index);
    }
    // Only want to log this when the card becomes visible
  }, [isVisible]);

  return (
    <div ref={container}>
      <TrailStandardCard
        key={trail.ID}
        isCompleted={isComplete('trail', trail.ID)}
        isFavorite={isFavorite('trail', trail.ID)}
        isVerified={isVerified('trail', trail.ID)}
        languageRegionCode={languageRegionCode}
        lazyLoadImages
        blockNavigation
        onCardClick={onCardClick}
        onCardMouseEnter={onCardMouseEnterInternal}
        onCardMouseLeave={onCardMouseLeave}
        onCompletionBadgeClick={onCompletionBadgeClick}
        onFavoriteClick={() => handleFavoriteClick('trail', trail.ID)}
        profilePhoto={trail.profile_photo_url}
        trail={cardTrail}
        url={exploreTrailUrl(cardTrail.slug)}
        maxPhotos={trail.num_photos}
      />
    </div>
  );
};

export default memo(ListViewTrailCard);
